/**
 * "About" descriptions for the MVX Plus markers — one plain-language,
 * non-diagnostic paragraph per marker, mirroring the app's About-tab content
 * (cat 700000+ block). MVX is NOT a Section and doesn't flow through the
 * Section-keyed About tab (findAboutConsideration), so these live in a STATIC
 * module (like holobiomeKits.ts / mvxSupplements.ts) and render via
 * MvxAboutSection. The same text is mirrored in the About-tab spreadsheet
 * (Section 15, categoryId 701500+) so it can move to the DB later.
 *
 * `id` matches the index/factor id (MvxInputs keys) so the About tab can show
 * only the markers the client has a value for.
 */
export interface MvxAboutEntry {
  id: string;
  title: string;
  description: string;
}

export const MVX_ABOUT: MvxAboutEntry[] = [
  {
    id: 'mvx',
    title: 'MVX (Metabolic Vulnerability Index)',
    description:
      'A composite score from 1 to 100 that summarizes near-term metabolic resilience, read by NMR from six blood markers and combined into two sub-scores — inflammation (IVX) and metabolic strain (MMX). A lower score reflects greater resilience; a higher score reflects more vulnerability.',
  },
  {
    id: 'ivx',
    title: 'IVX (Inflammation Vulnerability Index)',
    description:
      'One of the two MVX sub-scores. It reflects how much inflammation or hidden infection the body is carrying, drawn from GlycA and small HDL particle number. A lower value reflects less inflammatory burden.',
  },
  {
    id: 'mmx',
    title: 'MMX (Metabolic Malnutrition Index)',
    description:
      'One of the two MVX sub-scores. It reflects whether the cells are well-nourished and well-fueled for energy, drawn from citrate and the branched-chain amino acids. A lower value reflects better cellular nourishment and energy supply.',
  },
  {
    id: 'glycA',
    title: 'GlycA',
    description:
      'An NMR-based marker of systemic inflammation. It reflects the overall inflammatory signal in the blood and tends to rise with active inflammation or hidden infection. Lower values reflect a calmer inflammatory state.',
  },
  {
    id: 'sHdlP',
    title: 'Small HDL-P (Small HDL Particle Number)',
    description:
      'Measures the number of small HDL particles in the blood. An adequate level is considered supportive here; low levels can accompany toxic burden, impaired detoxification, or active inflammation drawing these particles down.',
  },
  {
    id: 'citrate',
    title: 'Citrate',
    description:
      "A marker tied to mitochondrial function and cellular energy production. It reflects how steadily the cells' energy pathways are turning over, and is read alongside the amino acids as part of the metabolic sub-score.",
  },
  {
    id: 'valine',
    title: 'Valine',
    description:
      'One of the three branched-chain amino acids (with leucine and isoleucine). It reflects protein and nutrient status and how the body builds and uses amino acids. Values within the optimal band reflect balanced handling — both very low and very high can reflect metabolic strain.',
  },
  {
    id: 'leucine',
    title: 'Leucine',
    description:
      'One of the three branched-chain amino acids (with valine and isoleucine). It supports muscle protein and energy metabolism and reflects protein and nutrient status. Values within the optimal band reflect balanced amino-acid handling.',
  },
  {
    id: 'isoleucine',
    title: 'Isoleucine',
    description:
      'One of the three branched-chain amino acids (with valine and leucine). It reflects protein and nutrient status and amino-acid metabolism. Values within the optimal band reflect balanced amino-acid handling.',
  },
];
